import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import CompanyInformationForm from "../components/CompanyInformationForm";
import CompanyAddressForm from "../components/CompanyAddressForm";
import ParentBranchForm from "../components/CompanyBranchForm";
import BypassStep from "../components/BypassStep";
import ClientInformationForm from "../components/ClientInformationForm.js";
import FinancialAbility from "../components/CompanyFinancialAbility";
import AdditionalInfo from "../components/CompanyAditionalInfo";
import TradeInformationForm from "../components/CompanyTradeInformationForm.js";
import SidebarManager from "../components/SidebarManager.js";
import Header from "../components/Header.js";
import { useAuth } from "../context/AuthContext.js";
import { getCompanyDetailsById } from "../services/companyService";
import { useToast } from "../context/ToastContext";
import UserRoles from "../constants/userRoles.js";

function FormPage() {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const companyId = searchParams.get("companyId");
  const versionId = searchParams.get("versionId");
  const [step, setStep] = useState(1);
  const [isEditable, setIsEditable] = useState(
    [UserRoles.MANAGER.id, UserRoles.VALIDATOR.id].includes(user.userRole.id)
  );
  const [companyData, setCompanyData] = useState({});
  const toast = useToast();
  const totalSteps = 7;

  const steps = [
    { number: 1, title: "Company Information" },
    { number: 2, title: "Address" },
    { number: 3, title: "Branch Information" },
    { number: 4, title: "Trade Information" },
    { number: 5, title: "Financial Ability" },
    { number: 6, title: "Additional Info" },
    { number: 7, title: "Client Information" },
  ];

  const today = new Date().toLocaleDateString("en-GB", {
    weekday: "long",
    day: "2-digit",
    month: "short",
    year: "numeric",
  });

  const renderProgress = () => (
    <div className="flex justify-between items-center mb-10 relative">
      {steps.map((s, idx) => {
        const isCompleted = step > s.number;
        const isCurrent = step === s.number;

        return (
          <div
            key={s.number}
            className="relative flex-1 flex flex-col items-center"
          >
            {/* LEFT HALF LINE (except first step) */}
            {idx !== 0 && (
              <div
                className={`absolute top-4 left-0 h-1 w-1/2 z-0 ${
                  isCompleted ? "bg-[#6F81F5]" : "bg-gray-300"
                }`}
              />
            )}

            {/* STEP CIRCLE */}
            <div
              className={`w-8 h-8 z-10 rounded-full border-2 flex items-center justify-center transition-colors duration-300 ${
                isCompleted
                  ? "bg-[#6F81F5] border-[#6F81F5] text-white"
                  : isCurrent
                  ? "bg-white border-[#6F81F5] text-[#6F81F5]"
                  : "bg-white border-gray-300 text-gray-400"
              }`}
            >
              {s.number}
            </div>

            {/* RIGHT HALF LINE (except last step) */}
            {idx !== steps.length - 1 && (
              <div
                className={`absolute top-4 right-0 h-1 w-1/2 z-0 ${
                  step > s.number ? "bg-[#6F81F5]" : "bg-gray-300"
                }`}
              />
            )}

            {/* STEP LABEL */}
            <div
              className={`mt-2 text-sm text-center w-max ${
                isCurrent || isCompleted
                  ? "text-[#6F81F5] font-medium"
                  : "text-gray-400"
              }`}
            >
              {s.title}
            </div>
          </div>
        );
      })}
    </div>
  );

  const fetchCompanyDetails = async () => {
    try {
      const data = await getCompanyDetailsById(companyId);
      setCompanyData(data);
      if (isEditable && data?.hasFormBeenSubmitted) {
        setIsEditable(false);
        if (user.userRole.id === UserRoles.VALIDATOR.id) {
          toast.warning("Form is under review, edit is restricted.");
        }
      }
    } catch (err) {
      console.error("Error loading company data", err);
    }
  };

  // useEffect(() => {
  //   const editable = user.userRole.id !== 1;
  //   console.log("Is Editable:" + editable);
  //   setIsEditable(editable);
  // }, []);

  useEffect(() => {
    // Only call if companyId is available
    if (companyId) {
      fetchCompanyDetails();
    }
  }, [companyId]);

  if (!companyId) {
    return (
      <p className="text-red-500 p-4">Error: companyId is missing in the URL</p>
    );
  }

  return (
    <div className="py-8 px-[5rem] bg-[#fbfbfb] w-screen mx-auto">
      <Header title="Assign Forms" date={today} />
      {renderProgress()}
      {step === 1 && (
        <CompanyInformationForm
          companyId={companyId}
          versionId={versionId}
          onNext={() => setStep(2)}
          isEditable={isEditable}
          currentStep={1}
          totalSteps={totalSteps}
        />
      )}

      {step === 2 && (
        <CompanyAddressForm
          companyId={companyId}
          versionId={versionId}
          onNext={() => setStep(3)}
          onBack={() => setStep(1)}
          isEditable={isEditable}
          currentStep={2}
          totalSteps={totalSteps}
        />
      )}

      {/* Future steps */}
      {step === 3 && (
        <ParentBranchForm
          companyId={companyId}
          versionId={versionId}
          onNext={() => setStep(4)}
          onBack={() => setStep(2)}
          isEditable={isEditable}
          currentStep={3}
          totalSteps={totalSteps}
        />
      )}
      {step === 4 && (
        <TradeInformationForm
          companyId={companyId}
          versionId={versionId}
          onNext={() => setStep(5)}
          onBack={() => setStep(3)}
          isEditable={isEditable}
          currentStep={4}
          totalSteps={totalSteps}
        />
      )}
      {step === 5 && (
        <FinancialAbility
          companyId={companyId}
          versionId={versionId}
          onNext={() => setStep(6)}
          onBack={() => setStep(4)}
          isEditable={isEditable}
          currentStep={5}
          totalSteps={totalSteps}
        />
      )}
      {step === 6 && (
        <AdditionalInfo
          companyId={companyId}
          versionId={versionId}
          onNext={() => setStep(7)}
          onBack={() => setStep(5)}
          isEditable={isEditable}
          currentStep={6}
          totalSteps={totalSteps}
        />
      )}
      {step === 7 && (
        <ClientInformationForm
          companyId={companyId}
          versionId={versionId}
          onBack={() => setStep(6)}
          isEditable={isEditable}
          currentStep={7}
          totalSteps={totalSteps}
        />
      )}
    </div>
  );
}

export default FormPage;
